import { z } from 'zod';

/**
 * Mirrors the server-side CreateTicketCommand and CreateTicketValidator.
 * Kept in lockstep with the API by hand for now; when we wire up
 * NSwag/Kiota codegen this file becomes generated.
 *
 * Validation duplicates a subset of the server rules so the user gets
 * immediate feedback. The server is still the source of truth — anything
 * that slips through here gets rejected with a 400 + ProblemDetails.
 */

// Full set including the email-only 'Uncategorized' — used for display/typing
// of tickets the API returns.
export const ticketCategory = z.enum(['Hardware', 'Application', 'Reports', 'Networking', 'Uncategorized']);
export type TicketCategory = z.infer<typeof ticketCategory>;

// What a user may choose on the submit form. 'Uncategorized' is reserved for
// email-origin tickets, so it's intentionally excluded here.
export const submittableCategory = z.enum(['Hardware', 'Application', 'Reports', 'Networking']);
export type SubmittableCategory = z.infer<typeof submittableCategory>;

export const ticketPriority = z.enum(['Low', 'Normal', 'High', 'Urgent']);
export type TicketPriority = z.infer<typeof ticketPriority>;

const BANNED_PHRASES = [
  'see email',
  'see my email',
  'see attached email',
  'check email',
  'look at email',
  'in email'
] as const;

function capsRatio(text: string): number {
  let letters = 0;
  let upper = 0;
  for (const ch of text) {
    if (!/[a-zA-Z]/.test(ch)) continue;
    letters++;
    if (ch === ch.toUpperCase() && ch !== ch.toLowerCase()) upper++;
  }
  return letters < 20 ? 0 : upper / letters;
}

function containsBannedPhrase(text: string): boolean {
  const lower = text.toLowerCase();
  return BANNED_PHRASES.some((p) => lower.includes(p));
}

export const createTicketSchema = z
  .object({
    subject: z
      .string()
      .min(5, 'Subject must be at least 5 characters.')
      .max(200, 'Subject is too long.'),
    // Required: the user must pick a category (no default). Friendly message
    // whether the value is missing or somehow not one of the allowed options.
    category: z.enum(['Hardware', 'Application', 'Reports', 'Networking'], {
      errorMap: () => ({ message: 'Please choose a category.' })
    }),
    application: z.string().max(80).optional().or(z.literal('').transform(() => undefined)),
    priority: ticketPriority.default('Normal'),
    bodyText: z
      .string()
      .min(20, 'Please add more detail — at least 20 characters.')
      .max(10_000, 'Body is too long.')
  })
  .superRefine((data, ctx) => {
    if (data.category === 'Application' && !data.application) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['application'],
        message: "Pick the application you're having trouble with."
      });
    }
    const combined = `${data.subject} ${data.bodyText}`;
    if (containsBannedPhrase(combined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bodyText'],
        message:
          'Please describe the issue in the ticket itself — "see email"-style submissions are not accepted.'
      });
    }
    if (capsRatio(combined) > 0.5) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bodyText'],
        message: "Please don't write in ALL CAPS. Use normal capitalization."
      });
    }
  });

export type CreateTicketInput = z.infer<typeof createTicketSchema>;

export type TicketStatus =
  | 'Pending'
  | 'InProgress'
  | 'WaitingOnSomeone'
  | 'PendingSoftwareRelease'
  | 'Completed';

export interface CreateTicketResponse {
  id: string;
  ticketNumber: number;
  subject: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  createdUtc: string;
}

export interface MyTicketsListItem {
  id: string;
  ticketNumber: number;
  subject: string;
  category: TicketCategory;
  application: string | null;
  priority: TicketPriority;
  status: TicketStatus;
  assigneeId: string | null;
  assigneeDisplayName: string | null;
  createdUtc: string;
  updatedUtc: string;
}

export interface Attachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  isInline: boolean;
}

export type TicketMessageKind = 'PublicReply' | 'InternalNote' | 'System';

export interface TicketMessage {
  id: string;
  authorId: string;
  authorEntraObjectId: string;
  authorDisplayName: string;
  kind: TicketMessageKind;
  bodyJson: string;
  bodyHtml: string;
  bodyText: string;
  createdUtc: string;
  attachments: Attachment[];
}

export interface TicketWatcher {
  id: string;
  entraObjectId: string;
  displayName: string;
  email: string;
}

/** A directory user surfaced by the watcher people-picker (Graph search). */
export interface DirectoryUser {
  oid: string;
  displayName: string;
  email: string | null;
  jobTitle: string | null;
}

export interface TicketDetail {
  id: string;
  ticketNumber: number;
  subject: string;
  category: TicketCategory;
  application: string | null;
  priority: TicketPriority;
  status: TicketStatus;
  descriptionJson: string;
  descriptionHtml: string;
  descriptionText: string;
  requesterId: string;
  requesterEntraObjectId: string;
  requesterDisplayName: string;
  requesterEmail: string;
  assigneeId: string | null;
  assigneeDisplayName: string | null;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  autoCloseArmed: boolean;
  autoCloseDueAt: string | null;
  createdUtc: string;
  updatedUtc: string;
  watchers: TicketWatcher[];
  messages: TicketMessage[];
  attachments: Attachment[];
}

export interface PostMessageInput {
  bodyJson: string;
  bodyHtml: string;
  bodyText: string;
  kind: TicketMessageKind;
  attachmentIds?: string[];
  /** Users to @-mention. Only honored when kind=PublicReply and the caller is IT. */
  mentionedUserIds?: string[];
}

/** One row in the IT queue (all tickets). Like MyTicketsListItem + requester. */
export interface QueueTicketListItem {
  id: string;
  ticketNumber: number;
  subject: string;
  category: TicketCategory;
  application: string | null;
  priority: TicketPriority;
  status: TicketStatus;
  requesterDisplayName: string;
  assigneeId: string | null;
  assigneeDisplayName: string | null;
  createdUtc: string;
  updatedUtc: string;
}

export interface QueueFilters {
  status?: TicketStatus;
  assignedToMe?: boolean;
}

/** An assignable IT user. `isMe` flags the calling agent (server-resolved). */
export interface AgentListItem {
  id: string;
  entraObjectId: string;
  displayName: string;
  email: string;
  isMe: boolean;
}

export interface AssignTicketResponse {
  assigneeId: string | null;
  assigneeDisplayName: string | null;
  status: TicketStatus;
}

export interface ChangeStatusResponse {
  status: TicketStatus;
  resolvedAt: string | null;
}
